/**
 * SwivuFile — canonical frontend representation of a Swivu File record.
 *
 * All fields are camelCase. The service layer transforms the snake_case API
 * response into this shape so the rest of the app never deals with raw API
 * casing.
 *
 * The optional `isOptimistic` flag is set on placeholder entries created
 * during an upload-in-progress; consumers can use it to render a loading
 * state instead of a real thumbnail.
 */

/** Canonical sources from GET /file/list. Legacy API value `manual` maps to `swivu`. */
export type FileSource = "swivu" | "companycam";
export type FileType = "image" | "document";

/** Ordering values accepted by GET /file/list `ordering` param. */
export type FileListOrdering =
  | "uploaded_at"
  | "-uploaded_at"
  | "updated_at"
  | "-updated_at"
  | "order"
  | "-order"
  | "id"
  | "-id";

export interface SwivuFile {
  id: number;
  /** Foreign key to the Document/S3 record */
  document: number | null;
  url: string;
  thumbnailUrl: string | null;
  name: string;
  task: number | null;
  resource: number | null;
  client: number | null;
  location: number | null;
  order: number;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  creator: number | null;
  updater: number | null;
  /** Display name from list API when present */
  creatorName: string | null;
  updaterName: string | null;
  sizeBytes: number | null;
  board: number;
  fileType: FileType;
  source: FileSource;
  externalId: string | null;
  canViewGrant: number;
  canEditGrant: number;
  isAnnotated: boolean;
  hasPermission: boolean | null;
  canAnalyze: boolean | null;
  /** True for placeholder entries injected before the upload resolves */
  isOptimistic?: boolean;
}

// ---------------------------------------------------------------------------
// Raw API response shape (snake_case — only used in the service layer)
// ---------------------------------------------------------------------------

export interface SwivuFileApiResponse {
  id: number;
  document: number | null;
  url: string;
  thumbnail_url: string | null;
  name: string;
  task: number | null;
  resource: number | null;
  client: number | null;
  location: number | null;
  order: number;
  description: string | null;
  uploaded_at: string;
  /** @deprecated Legacy API field — remove after backend rollout completes */
  created_at?: string;
  updated_at: string;
  creator: number | null;
  updater: number | null;
  creator_name?: string | null;
  updater_name?: string | null;
  size_bytes: number | null;
  board: number;
  file_type: FileType;
  /** API may still emit legacy `manual` for Swivu-native uploads */
  source: FileSource | "manual";
  external_id: string | null;
  can_view_grant: number;
  can_edit_grant: number;
  is_annotated: boolean;
  has_permission?: boolean | null;
  can_analyze?: boolean | null;
}

export interface SwivuFileListApiResponse {
  count: number;
  results: SwivuFileApiResponse[];
}

export interface SwivuFileListPage {
  count: number;
  results: SwivuFile[];
}

// ---------------------------------------------------------------------------
// Mapper
// ---------------------------------------------------------------------------

/** Normalize API / legacy source to the frontend FileSource contract. */
export function mapApiSourceToFileSource(source: FileSource | "manual" | string | null | undefined): FileSource {
  if (source === "companycam") return "companycam";
  return "swivu";
}

/** Map frontend source filter to the API query value (`swivu` | `companycam`). */
export function mapFileSourceToApi(source: FileSource): "swivu" | "companycam" {
  return source === "companycam" ? "companycam" : "swivu";
}

export function mapApiFileToSwivuFile(raw: SwivuFileApiResponse): SwivuFile {
  return {
    id: raw.id,
    document: raw.document,
    url: raw.url,
    thumbnailUrl: raw.thumbnail_url,
    name: raw.name,
    task: raw.task,
    resource: raw.resource,
    client: raw.client,
    location: raw.location,
    order: raw.order,
    description: raw.description,
    createdAt: raw.uploaded_at ?? raw.created_at ?? "",
    updatedAt: raw.updated_at,
    creator: raw.creator,
    updater: raw.updater,
    creatorName: raw.creator_name ?? null,
    updaterName: raw.updater_name ?? null,
    sizeBytes: raw.size_bytes,
    board: raw.board,
    fileType: raw.file_type,
    source: mapApiSourceToFileSource(raw.source),
    externalId: raw.external_id,
    canViewGrant: raw.can_view_grant,
    canEditGrant: raw.can_edit_grant,
    isAnnotated: raw.is_annotated,
    hasPermission: raw.has_permission ?? null,
    canAnalyze: raw.can_analyze ?? null,
  };
}
